import type { MessageKey } from "@wato/i18n";
import type { ConsoleMessageView } from "../../../api/console-types";

export const key = (part: string) => `dashboard.console.${part}` as MessageKey;

export type Tr = (k: string, params?: Record<string, string | number>) => string;

export const str = (v: unknown): string => (typeof v === "string" ? v : "");

export const asRecord = (v: unknown): Record<string, unknown> =>
  v && typeof v === "object" ? (v as Record<string, unknown>) : {};

/** Summaries beyond the latest few sit behind a toggle. */
export const SUMMARIES_SHOWN = 3;

export const SECTIONS = ["summaries", "questions", "proposals"] as const;
export type InboxSection = (typeof SECTIONS)[number];

const SECTION_KINDS: Record<InboxSection, readonly ConsoleMessageView["kind"][]> = {
  summaries: ["summary"],
  questions: ["unknown_question"],
  proposals: ["proposal", "proposal_result"],
};

/** OWN-317: alerts (`notification`) stay in the bell; chat kinds live in the assistant chat tab. */
export const inSection = (kind: ConsoleMessageView["kind"], section: InboxSection): boolean =>
  SECTION_KINDS[section].includes(kind);

/** Ids of summaries past the latest `SUMMARIES_SHOWN` (input is newest first, as served). */
export function olderSummaryIds(items: readonly ConsoleMessageView[]): Set<string> {
  return new Set(
    items
      .filter((m) => m.kind === "summary")
      .slice(SUMMARIES_SHOWN)
      .map((m) => m.id),
  );
}

/** Built-in command labels are catalog keys; builder proposals carry ready text. */
export const rowText = (tr: Tr, v: string | null): string => {
  if (v === null) return "";
  return v === "on" || v === "off" ? tr(`diff.value.${v}`) : v;
};

/** `20261018` -> a locale-formatted date. */
export function dayLabel(day: number, locale: string): string {
  const y = Math.floor(day / 10000);
  const mo = Math.floor(day / 100) % 100;
  const d = day % 100;
  return new Intl.DateTimeFormat(locale === "ar" ? "ar-u-nu-latn" : "en", {
    dateStyle: "medium",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(y, mo - 1, d)));
}

/** Card title/body come from the payload; a card without a title falls back to nothing. */
export function cardText(
  m: ConsoleMessageView,
  t: (k: MessageKey) => string,
): { title: string; body: string } {
  const p = m.payload;
  const body = str(p.text) || str(p.body) || str(p.message) || str(p.question) || str(p.summary);
  let title = str(p.title);
  const titleKey = str(p.titleKey);
  if (!title && titleKey) {
    const text = t(titleKey as MessageKey);
    if (text && text !== titleKey) title = text;
  }
  return { title, body };
}
